import React from 'react';
import { CommentListEventItem } from './CommentListEventItem';
import { Trans } from '@lingui/react/macro';
import { faFilePlus } from '@fortawesome/pro-light-svg-icons';
import { useIsBuyer, useAsyncState, useGetActiveProjectId } from '@/utils/hooks';
import { getOfferProfileName } from '@/services/buyer-dashboard';
import { CommentMessage } from '@ygtechnologies/bygr-frontend-chat';

interface CommentListEventItemOfferProps {
    comment: CommentMessage;
}

interface Data {
    offerId: number;
    changeRequestId: number;
    status: Status;
}

enum Status {
    Sent = 1,
    Rejected = 2,
    Retracted = 3,
    Approved = 4,
}

const getUrl = (isBuyer: boolean, data: Data, projectId: number | undefined) => {
    if (isBuyer) {
        return `/change-requests/${data.changeRequestId}/offer`;
    } else {
        return `/ext/change-request/${data.changeRequestId}`;
    }
};

//TODO: use the status translations?
const getVerb = (status: Status, isBuyer: boolean) => {
    switch (status) {
        case Status.Sent:
            if (isBuyer) {
                return <Trans>received</Trans>;
            } else {
                return <Trans>sent</Trans>;
            }
        case Status.Rejected:
            return <Trans>rejected</Trans>;
        case Status.Retracted:
            return <Trans>retracted</Trans>;
        case Status.Approved:
            return <Trans>accepted</Trans>;
    }
};

const getTopText = (comment: CommentMessage, data: Data, isBuyer: boolean, profileName: string | null) => {
    switch (data.status) {
        case Status.Sent:
            if (isBuyer) {
                return (
                    <Trans>
                        Offer received from {profileName} for request #{data.changeRequestId}
                    </Trans>
                );
            } else {
                return (
                    <Trans>
                        Offer sent from {profileName} for request #{data.changeRequestId}
                    </Trans>
                );
            }
        case Status.Rejected:
            return (
                <Trans>
                    Offer from {profileName} for request #{data.changeRequestId} rejected
                </Trans>
            );
        case Status.Retracted:
            return (
                <Trans>
                    Offer from {profileName} for request #{data.changeRequestId} retracted
                </Trans>
            );
        case Status.Approved:
            return (
                <Trans>
                    Offer from {profileName} for request #{data.changeRequestId} accepted
                </Trans>
            );
    }
};

export const CommentListEventItemOffer: React.FC<CommentListEventItemOfferProps> = ({ comment }) => {
    var data: Data = comment.data;
    var isBuyer = useIsBuyer();
    var currentProject = useGetActiveProjectId();
    var [profileName] = useAsyncState(
        async () => await getOfferProfileName(data.changeRequestId, data.offerId),
        [data.offerId],
        null
    );

    if (isBuyer && data.status === Status.Retracted) {
        return (
            <CommentListEventItem
                title={
                    <>
                        <Trans>Offer #{data.offerId}</Trans> {getVerb(data.status, isBuyer)}
                    </>
                }
                topText={getTopText(comment, data, isBuyer, profileName)}
                icon={faFilePlus}
            />
        );
    }

    return (
        <CommentListEventItem
            title={
                <>
                    <Trans>Offer #{data.offerId}</Trans> {getVerb(data.status, isBuyer)}
                </>
            }
            topText={getTopText(comment, data, isBuyer, profileName)}
            bottomText={<Trans>Offer #{data.offerId} can be reviewed via the offers tab</Trans>}
            buttonText={<Trans>Review offer</Trans>}
            url={getUrl(isBuyer, comment.data, currentProject)}
            icon={faFilePlus}
        />
    );
};
